import { Spherical } from "three/webgpu";
import type { Vector3 } from "three/webgpu";
import { CARTOGRAPHY_NORMAL, HERO_PHI, HERO_RADIUS, HERO_THETA } from "../config/worldFrame";

export const CAMERA_MOTION = {
  cosmosRadius: HERO_RADIUS,
  cosmosPhi: HERO_PHI,
  cosmosTheta: HERO_THETA,
  cosmosMinRadius: 34,
  cosmosMaxRadius: 62,
  cosmosMinPhi: 0.82,
  cosmosMaxPhi: 2.02,
  blackHoleFocusPhi: 1.2,
  blackHoleFocusTheta: -0.18,
  blackHoleMinRadius: 9.5,
  blackHoleMaxRadius: 30,
  blackHoleMinPhi: 0.78,
  blackHoleMaxPhi: 2.04,
  overviewRotateTheta: 0.0052,
  overviewRotatePhi: 0.0044,
  blackHoleRotateTheta: 0.006,
  blackHoleRotatePhi: 0.005,
  focusRotateTheta: 0.006,
  focusRotatePhi: 0.0052,
  focusMinPhi: 0.48,
  focusMaxPhi: Math.PI - 0.48,
  overviewAutoOrbitTheta: 0.034,
  overviewIdleHomeResponse: 0.82,
  overviewIdleParallaxResponse: 2.8,
  demoFocusRotateTheta: 0.072,
  demoFocusRotatePhi: 0.018,
  demoBlackHoleRotateTheta: 0.068,
  demoTravelArcRatio: 0.11,
  demoTravelLiftRatio: 0.045,
  epsilon: 0.0001,
} as const;

export const DEFAULT_FOCUS_DIRECTION = CARTOGRAPHY_NORMAL.clone();
export const FOCUS_ROTATE_THETA = CAMERA_MOTION.focusRotateTheta;
export const FOCUS_ROTATE_PHI = CAMERA_MOTION.focusRotatePhi;

export function nearestEquivalentTheta(reference: number, canonical: number) {
  const fullTurn = Math.PI * 2;
  return canonical + Math.round((reference - canonical) / fullTurn) * fullTurn;
}

export function rebaseOrbitTheta(current: Spherical, target: Spherical) {
  const fullTurn = Math.PI * 2;
  const threshold = fullTurn * 64;
  if (Math.abs(target.theta) < threshold) return;
  const offset = Math.trunc(target.theta / fullTurn) * fullTurn;
  current.theta -= offset;
  target.theta -= offset;
}

export function rotateDirection(direction: Vector3, scratch: Spherical, thetaDelta: number, phiDelta: number) {
  scratch.setFromVector3(direction);
  scratch.theta += thetaDelta;
  scratch.phi = Math.max(CAMERA_MOTION.focusMinPhi, Math.min(CAMERA_MOTION.focusMaxPhi, scratch.phi + phiDelta));
  direction.setFromSpherical(scratch).normalize();
}

export function captureSceneBearing(cameraPosition: Vector3, target: Vector3, scratch: Vector3, bearing: Vector3) {
  scratch.copy(cameraPosition).sub(target);
  bearing.copy(scratch.lengthSq() < CAMERA_MOTION.epsilon ? DEFAULT_FOCUS_DIRECTION : scratch.normalize());
}

export function captureBlackHoleOrbit(
  cameraPosition: Vector3,
  focusPoint: Vector3,
  fallbackRadius: number,
  orbit: Spherical,
  target: Spherical,
  scratchOffset: Vector3,
) {
  scratchOffset.copy(cameraPosition).sub(focusPoint);
  if (scratchOffset.lengthSq() < CAMERA_MOTION.epsilon) {
    orbit.set(fallbackRadius, CAMERA_MOTION.blackHoleFocusPhi, CAMERA_MOTION.blackHoleFocusTheta);
    target.copy(orbit);
    return;
  }
  const spherical = new Spherical().setFromVector3(scratchOffset);
  orbit.radius = Math.max(CAMERA_MOTION.blackHoleMinRadius, Math.min(CAMERA_MOTION.cosmosMaxRadius, spherical.radius));
  orbit.phi = Math.max(CAMERA_MOTION.blackHoleMinPhi, Math.min(CAMERA_MOTION.blackHoleMaxPhi, spherical.phi));
  orbit.theta = spherical.theta;
  target.radius = Math.max(
    CAMERA_MOTION.blackHoleMinRadius,
    Math.min(CAMERA_MOTION.blackHoleMaxRadius, fallbackRadius),
  );
  target.phi = orbit.phi;
  target.theta = spherical.theta;
}
